/**
 * The look words of the music video editor (spec `2026-10-09-music24-music-video-editor-design.md`
 * §4.1, §4.5) and their map to the words of the kit `AssetPlan` of music24-api.
 *
 * One table for the web preview and the server render: the browser puts the CSS filter of a grade
 * on the preview, and the renderer (`AiReel`) puts the same filter on the footage. So the made
 * video looks like the preview. The filter values are the `.g-*` classes of the design
 * (`docs/superpowers/design-reference/music24-music-video/mv.css`).
 *
 * Only a type import from `releaseThemes.ts`, so every project may read this file.
 */
import type { ThemeSegmentMotion, ThemeTransition } from './releaseThemes';

// ---------------------------------------------------------------------------------------------
// Grades
// ---------------------------------------------------------------------------------------------

/** The colour grade of the whole video. `none` shows the footage as it is. */
export const MUSIC_VIDEO_GRADES = ['film', 'noir', 'neon', 'warm', 'cold', 'none'] as const;
export type MusicVideoGrade = (typeof MUSIC_VIDEO_GRADES)[number];

/** The grade of a new cut. */
export const DEFAULT_MUSIC_VIDEO_GRADE: MusicVideoGrade = 'film';

/** The CSS filter of each grade. The preview and the render use the same text. */
export const MUSIC_VIDEO_GRADE_FILTERS: Readonly<Record<MusicVideoGrade, string>> = {
  film: 'contrast(1.08) saturate(.85) sepia(.18)',
  noir: 'grayscale(1) contrast(1.25)',
  neon: 'saturate(1.6) hue-rotate(-28deg) contrast(1.1)',
  warm: 'sepia(.35) saturate(1.2) brightness(1.05)',
  cold: 'hue-rotate(18deg) saturate(.8) brightness(.95)',
  none: 'none',
};

export function isMusicVideoGrade(value: unknown): value is MusicVideoGrade {
  return typeof value === 'string' && (MUSIC_VIDEO_GRADES as readonly string[]).includes(value);
}

/** The CSS filter of a grade. An unknown grade (a newer client) shows the footage as it is. */
export function musicVideoGradeFilter(grade: string): string {
  return isMusicVideoGrade(grade) ? MUSIC_VIDEO_GRADE_FILTERS[grade] : MUSIC_VIDEO_GRADE_FILTERS.none;
}

// ---------------------------------------------------------------------------------------------
// Clip words
// ---------------------------------------------------------------------------------------------

/** The camera move of one clip. `followFace` keeps the face in the frame; the render shows it still. */
export const MUSIC_VIDEO_MOVES = ['still', 'pushIn', 'pullOut', 'panLeft', 'panRight', 'followFace'] as const;
export type MusicVideoMove = (typeof MUSIC_VIDEO_MOVES)[number];

/** How a clip starts. */
export const MUSIC_VIDEO_CUT_INS = ['cut', 'flash', 'fade', 'whip'] as const;
export type MusicVideoCutIn = (typeof MUSIC_VIDEO_CUT_INS)[number];

/** `fill` crops the clip to the frame; `blur` shows the whole clip over a blurred copy of it. */
export const MUSIC_VIDEO_FITS = ['fill', 'blur'] as const;
export type MusicVideoFit = (typeof MUSIC_VIDEO_FITS)[number];

/** The play speed of a clip. */
export const MUSIC_VIDEO_SPEEDS = [0.5, 1, 2] as const;
export type MusicVideoSpeed = (typeof MUSIC_VIDEO_SPEEDS)[number];

/** The camera move in the words of the plan (`motion` of a segment). */
export const MUSIC_VIDEO_MOVE_TO_MOTION: Readonly<Record<MusicVideoMove, ThemeSegmentMotion>> = {
  still: 'static',
  pushIn: 'push',
  pullOut: 'pull',
  panLeft: 'pan-left',
  panRight: 'pan-right',
  followFace: 'static',
};

/** The start of a clip in the words of the plan (`transitionIn` of a segment). */
export const MUSIC_VIDEO_CUT_IN_TO_TRANSITION: Readonly<Record<MusicVideoCutIn, ThemeTransition>> = {
  cut: 'cut',
  flash: 'flash',
  fade: 'dissolve',
  whip: 'punch',
};

export function isMusicVideoMove(value: unknown): value is MusicVideoMove {
  return typeof value === 'string' && (MUSIC_VIDEO_MOVES as readonly string[]).includes(value);
}

export function isMusicVideoCutIn(value: unknown): value is MusicVideoCutIn {
  return typeof value === 'string' && (MUSIC_VIDEO_CUT_INS as readonly string[]).includes(value);
}

export function isMusicVideoFit(value: unknown): value is MusicVideoFit {
  return typeof value === 'string' && (MUSIC_VIDEO_FITS as readonly string[]).includes(value);
}

export function isMusicVideoSpeed(value: unknown): value is MusicVideoSpeed {
  return typeof value === 'number' && (MUSIC_VIDEO_SPEEDS as readonly number[]).includes(value);
}

/** The plan motion of a move. An unknown move is `static`. */
export function motionOfMove(move: string): ThemeSegmentMotion {
  return isMusicVideoMove(move) ? MUSIC_VIDEO_MOVE_TO_MOTION[move] : 'static';
}

/** The plan transition of a cut-in. An unknown cut-in is a plain `cut`. */
export function transitionOfCutIn(cutIn: string): ThemeTransition {
  return isMusicVideoCutIn(cutIn) ? MUSIC_VIDEO_CUT_IN_TO_TRANSITION[cutIn] : 'cut';
}

// ---------------------------------------------------------------------------------------------
// Text words
// ---------------------------------------------------------------------------------------------

/** The style of the lyric captions. */
export const MUSIC_VIDEO_LYRIC_STYLES = ['clean', 'caps', 'serif', 'word'] as const;
export type MusicVideoLyricStyle = (typeof MUSIC_VIDEO_LYRIC_STYLES)[number];

/** Where the lyric captions sit. */
export const MUSIC_VIDEO_LYRIC_PLACES = ['bottom', 'middle'] as const;
export type MusicVideoLyricPlace = (typeof MUSIC_VIDEO_LYRIC_PLACES)[number];

/** Which lines show. `choruses`: only the lines inside the chorus sections of the song map. */
export const MUSIC_VIDEO_LYRIC_WHEN = ['all', 'choruses'] as const;
export type MusicVideoLyricWhen = (typeof MUSIC_VIDEO_LYRIC_WHEN)[number];

/** The call to action of the end card. `auto` picks it from the release date, as the long-video end card does. */
export const MUSIC_VIDEO_END_CARD_LINES = ['auto', 'outNow', 'preSave', 'listen'] as const;
export type MusicVideoEndCardLine = (typeof MUSIC_VIDEO_END_CARD_LINES)[number];

/**
 * The pixel size of a music video: 1920 × 1080 only (the `full169` spec). The 4K option is gone
 * (owner 2026-10-10): a 4K part did not finish in the part limit, and the footage is 1080p.
 */
export const MUSIC_VIDEO_SIZE_PX: Readonly<{ width: number; height: number }> = { width: 1920, height: 1080 };

/**
 * The longest text of one text box: a custom text, a title or name override, a title or chapter card
 * effect. `musicVideoDoc.ts` exports it too. It lives here so that `musicVideoEffects.ts` can read it
 * with no import cycle.
 */
export const MUSIC_VIDEO_TEXT_MAX_LENGTH = 200;
